import React, { useCallback, useEffect, useState } from "react";
import { useIsMutating, useQueryClient } from "@tanstack/react-query";

import { InfiniteTable, Loading } from "../../../components";
import { useDebouncedValue } from "../../../core/hooks";

import PriceItemRenderer from "./PriceItemRenderer";
import { Visible } from "../../../core/components";
import UnapprovedPriceItemRenderer from "./UnapprovedPriceItemRenderer";
import { Segmented } from "../../../components/ui";
import PriceFilters from "./PriceFilters";
import { priceSortByOptions, sourceOptions } from "../../constants";
import { initialValues } from "./filtersFormConfig";
import AvailablePriceItemRenderer from "./AvailablePriceItemRenderer";
import { AiModal } from "../../../order/components/Quote/Leads";
import {
  PRODUCT_PRICE_LIST_KEY,
  PRODUCT_PRICE_LIST_TABS,
  useProductPriceList,
} from "../../hooks";

const HISTORICAL_HEADERS = [
  "",
  "",
  "Price Date",
  "Source",
  "Date",
  "Condition",
  "",
  "Price",
  "Box & Papers",
  "Supplier",
  "Store",
  "Country",
  "",
];

const AVAILABLE_HEADERS = [
  "",
  "",
  "Listed",
  "Source",
  "Date",
  "Condition",
  "",
  "Price",
  "Box & Papers",
  "Supplier",
  "Store",
  "Country",
  "",
];

const DEAL_FINDER_HEADERS = [
  "Price Date",
  "Source",
  "Year",
  "Condition",
  "Price",
  "Trade Median",
  "Match Confidence",
  "Box & Papers",
  "Group",
  "Sender name",
  "Sender phone",
  "Country",
  "",
];

const PriceListTable = ({
  product,
  tab,
  filters,
  onResultsLoaded,
  onItemRemoved,
  setModalOpen,
  setModalItem,
  className,
  headers,
  RowComponent,
}) => {
  const debouncedFilters = useDebouncedValue(filters, 300);
  const {
    items,
    total,
    isLoading,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
    isError,
  } = useProductPriceList(product?.id, tab, debouncedFilters, {
    enabled: !!product?.id,
  });

  useEffect(() => {
    onResultsLoaded?.(items, total);
  }, [items, total]);

  if (isLoading && items.length === 0) {
    return <Loading type="table" showText />;
  }

  if (isError && items.length === 0) {
    return (
      <div className="no-results">
        <p>Unable to load trading history. Try refreshing the page.</p>
      </div>
    );
  }

  // A product with no recorded prices rendered an InfiniteTable of nothing —
  // column headers over blank space, which reads as a broken panel rather than
  // an empty one. Normal for a store's own vintage entries, which start with no
  // trading history at all and may never get one from deals alone.
  if (items.length === 0) {
    return (
      <div className="no-results">
        <p>
          No trading history for this product yet. Prices appear here as deals
          are recorded, and can also be added manually.
        </p>
      </div>
    );
  }

  return (
    <InfiniteTable
      className={className}
      data={items}
      fetchNextPage={fetchNextPage}
      hasNextPage={hasNextPage ?? false}
      isFetchingNextPage={isFetchingNextPage}
      isInitialLoading={isLoading}
      fetchLimit={50}
      height={550}
      headers={headers}
      RowComponent={RowComponent}
      rowComponentProps={{
        onItemRemoved,
        setModalOpen,
        setModalItem,
      }}
      scrollableTarget="scrollableDiv"
    />
  );
};

const PriceList = ({ user, product, onFiltersChange, filters }) => {
  const queryClient = useQueryClient();
  const [priceCount, setPriceCount] = useState(0);
  const [activeItem, setActiveItem] = useState(0);
  const isMutating = useIsMutating(["createProductPrice", product?.id]);
  const [values, setValues] = useState(initialValues);
  const [isOpen, setIsOpen] = useState(false);
  const [item, setItem] = useState(null);

  const onHide = () => {
    setIsOpen(false);
  };

  // Segmented values are strings; the tab index stays the state (getTabDefaultValues keys on it).
  const tabs = ["Historical", "Available", "Deal finder"].map((label, index) => ({
    value: String(index),
    label,
    count: index === activeItem && priceCount !== 0 ? priceCount : undefined,
  }));

  const handleResultsLoaded = useCallback((_, total) => {
    setPriceCount(total);
  }, []);

  const invalidatePriceList = () => {
    queryClient.invalidateQueries({
      predicate: (query) =>
        query.queryKey[0] === PRODUCT_PRICE_LIST_KEY &&
        query.queryKey[1] === product?.id,
    });
  };

  const getTabDefaultValues = (tabIndex) => {
    let newValues = {};

    switch (tabIndex) {
      case 0:
        newValues = {
          year: undefined,
          box: undefined,
          condition: undefined,
          source: undefined,
          location: undefined,
          priceSortBy: priceSortByOptions[0]?.id,
        };
        break;

      case 1: {
        const selectedSource = sourceOptions.find(
          (opt) => opt.id === "chrono24"
        );
        const selectedSort = priceSortByOptions.find(
          (opt) => opt.id === "sort-by-price"
        );

        newValues = {
          year: undefined,
          box: undefined,
          condition: undefined,
          source: selectedSource?.id,
          location: undefined,
          priceSortBy: selectedSort?.id,
        };
        break;
      }

      case 2:
        newValues = {
          year: undefined,
          box: undefined,
          condition: undefined,
          source: undefined,
          location: undefined,
          priceSortBy: priceSortByOptions[0]?.id,
        };
        break;

      default:
        break;
    }

    return newValues;
  };

  useEffect(() => {
    const initialTabValues = getTabDefaultValues(activeItem);
    setValues(initialTabValues);
    onFiltersChange(initialTabValues);
  }, [activeItem]);

  if (!user?.is_admin || isMutating) {
    return null;
  }

  const sharedTableProps = {
    product,
    filters,
    onResultsLoaded: handleResultsLoaded,
    onItemRemoved: invalidatePriceList,
    setModalOpen: setIsOpen,
    setModalItem: setItem,
  };

  return (
    <>
      <Segmented
        label="Trading history"
        data-btn="price-filter"
        options={tabs}
        value={String(activeItem)}
        onChange={(value) => setActiveItem(Number(value))}
      />
      <PriceFilters
        key={`filters-${activeItem}`}
        {...{
          user,
          onFiltersChange,
          initialValues: values,
          product,
        }}
      />
      <Visible when={activeItem === 0}>
        <PriceListTable
          {...sharedTableProps}
          tab={PRODUCT_PRICE_LIST_TABS.historical}
          className="product-price-list"
          headers={HISTORICAL_HEADERS}
          RowComponent={PriceItemRenderer}
        />
      </Visible>
      <Visible when={activeItem === 1}>
        <PriceListTable
          {...sharedTableProps}
          tab={PRODUCT_PRICE_LIST_TABS.available}
          className="product-price-list"
          headers={AVAILABLE_HEADERS}
          RowComponent={AvailablePriceItemRenderer}
        />
      </Visible>
      <Visible when={activeItem === 2}>
        <PriceListTable
          {...sharedTableProps}
          tab={PRODUCT_PRICE_LIST_TABS.dealFinder}
          className="underpriced-unapproved"
          headers={DEAL_FINDER_HEADERS}
          RowComponent={UnapprovedPriceItemRenderer}
        />
      </Visible>
      <AiModal isOpen={isOpen} item={item} hide={onHide} hideImport />
    </>
  );
};

export default PriceList;
